import React, { useState, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    Typography,
    IconButton,
    Divider
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { FormProvider, RHFTextField, RHFSelectField } from '../../common/hook-form';
import { useSnackbar } from 'notistack';
import { apiConstants, postGeneric } from '../../common/constants';
import { getMerchant } from '../../helpers/ReduxHelpers';
import useGetCourier from '../dashboard/performance/useGetCourier';
import _ from 'lodash';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RHFMultipleSelectField from '../../common/hook-form/RHFMultipleSelectField';
import PageTitle from '../../common/PageTitle';
import PageHero from '../../common/PageHero';

// Validation schema
const validationSchema = Yup.object().shape({
    name: Yup.string().required('Rule name is required'),
    category: Yup.string().required('Category is required'),
    type: Yup.string().required('Type is required'),
    status: Yup.string().required('Status is required'),
    rules: Yup.array().of(
        Yup.object().shape({
            conditions: Yup.array().of(
                Yup.object().shape({
                    fact: Yup.string().required('Fact is required'),
                    operator: Yup.string().required('Operator is required'),
                    value: Yup.string().required('Value is required')
                })
            ).min(1, 'At least one condition is required'),
            result: Yup.object().shape({
                type: Yup.string().required('Result type is required'),
                values: Yup.array().of(Yup.string()).min(1, 'At least one value is required')
            })
        })
    ).min(1, 'At least one rule is required')
});

// Constants for form options
const categoryOptions = [
    { label: 'Order', value: 'ORDER' },
    { label: 'Shipment', value: 'SHIPMENT' }
];

const typeOptions = [
    { label: 'Custom', value: 'CUSTOM' },
    { label: 'Default', value: 'DEFAULT' }
];

const statusOptions = [
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Inactive', value: 'INACTIVE' }
];

const factOptions = [
    { label: 'Weight', value: 'WEIGHT' },
    { label: 'Order Value', value: 'ORDER_VALUE' },
    { label: 'Delivery Type', value: 'DELIVERY_TYPE' },
    { label: 'Payment Mode', value: 'PAYMENT_MODE' },
    { label: 'Pickup Pincode', value: 'PICKUP_PINCODE' },
    { label: 'Delivery Pincode', value: 'DELIVERY_PINCODE' }
];

const operatorOptions = [
    { label: 'Equals', value: '==' },
    { label: 'Not Equals', value: '!=' },
    { label: 'Greater Than', value: '>' },
    { label: 'Less Than', value: '<' },
    { label: 'Greater Than or Equal', value: '>=' },
    { label: 'Less Than or Equal', value: '<=' },
    { label: 'In', value: 'in' },
    { label: 'Not In', value: 'not in' }
];

const resultTypeOptions = [
    { label: 'Courier Partner', value: 'CourierPartner' },
    { label: 'Priority', value: 'Priority' }
];

const defaultValues = {
    name: '',
    category: 'ORDER',
    type: 'CUSTOM',
    status: 'ACTIVE',
    rules: [
        {
            conditions: [
                {
                    fact: '',
                    operator: '',
                    value: ''
                }
            ],
            result: {
                type: 'CourierPartner',
                values: []
            }
        }
    ]
};

function CustomRule() {
    const [loading, setLoading] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const merchant = getMerchant();
    const { data: allCouriers } = useGetCourier();

    // Get courier options for result values
    const courierOptions = useMemo(() => {
        return allCouriers && _.uniqBy(
            allCouriers.filter(val => val.supported_shipment === "B2C"),
            'parent'
        ).map(val => ({
            label: val.parent,
            value: val.parent,
        }));
    }, [allCouriers]);

    console.log(courierOptions);

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues
    });

    const { handleSubmit, control, watch, reset } = methods;

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            const payload = {
                ...data,
                merchantId: merchant.id
            };

            const response = await postGeneric(apiConstants.CREATE_CUSTOM_RULE, payload);

            if (response.status === 200) {
                enqueueSnackbar('Custom rule created successfully!', { variant: 'success' });
                reset(defaultValues);
            } else {
                enqueueSnackbar('Failed to create custom rule', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error creating custom rule:', error);
            enqueueSnackbar('Error creating custom rule', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="main-content" className="container-fluid">
            <PageTitle subTitle="Define conditions to route shipments to specific couriers">Create Custom Rule</PageTitle>
            {/* <PageHero title='Create Custom Rule' subtitle='D' sx={{mb: 2.5}} /> */}
            <Box sx={{ p: 3 }}>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                        {/* Basic Information */}
                        <Grid item xs={12}>
                            <Card>
                                <CardHeader title="Basic Information" />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <RHFTextField
                                                name="name"
                                                label="Rule Name"
                                                mandatory
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <RHFSelectField
                                                name="category"
                                                label="Category"
                                                options={categoryOptions}
                                                width="100%"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <RHFSelectField
                                                name="type"
                                                label="Type"
                                                options={typeOptions}
                                                width="100%"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <RHFSelectField
                                                name="status"
                                                label="Status"
                                                options={statusOptions}
                                                width="100%"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Rules Section */}
                        <Grid item xs={12}>
                            <RulesSection control={control} watch={watch} courierOptions={courierOptions} />
                        </Grid>

                        {/* Submit Button */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <LoadingButton
                                    type="submit"
                                    variant="contained"
                                    loading={loading}
                                    size="small"
                                    startIcon={<AddCircleOutlineIcon />}
                                >
                                    Create Rule
                                </LoadingButton>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => reset(defaultValues)}
                                    disabled={loading}
                                >
                                    Reset
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </FormProvider>
            </Box>
        </div>
    );
}

// Rules Section Component
function RulesSection({ control, watch, courierOptions }) {
    const { fields, append, remove } = useFieldArray({
        control,
        name: "rules"
    });

    const addRule = () => {
        append({
            conditions: [
                {
                    fact: '',
                    operator: '',
                    value: ''
                }
            ],
            result: {
                type: 'CourierPartner',
                values: []
            }
        });
    };

    return (
        <Card>
            <CardHeader
                title="Rules Configuration"
                action={
                    <Button
                        startIcon={<AddIcon />}
                        onClick={addRule}
                        variant="contained"
                        color="warning"
                        size="small"
                    >
                        Add Rule
                    </Button>
                }
            />
            <CardContent>
                {fields.map((rule, ruleIndex) => (
                    <RuleItem
                        key={rule.id}
                        ruleIndex={ruleIndex}
                        control={control}
                        watch={watch}
                        courierOptions={courierOptions}
                        onRemove={() => remove(ruleIndex)}
                        isLast={ruleIndex === fields.length - 1}
                    />
                ))}
            </CardContent>
        </Card>
    );
}

// Individual Rule Item Component
function RuleItem({ ruleIndex, control, watch, courierOptions, onRemove, isLast }) {
    const { fields, append, remove } = useFieldArray({
        control,
        name: `rules.${ruleIndex}.conditions`
    });

    const addCondition = () => {
        append({
            fact: '',
            operator: '',
            value: ''
        });
    };

    const currentRule = watch(`rules.${ruleIndex}`);

    return (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    Rule {ruleIndex + 1}
                </Typography>
                {!isLast && (
                    <IconButton onClick={onRemove} color="error" size="small">
                        <DeleteIcon />
                    </IconButton>
                )}
            </Box>

            {/* Conditions Section */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Conditions
                </Typography>
                {fields.map((condition, conditionIndex) => (
                    <ConditionItem
                        key={condition.id}
                        ruleIndex={ruleIndex}
                        conditionIndex={conditionIndex}
                        control={control}
                        watch={watch}
                        onRemove={() => remove(conditionIndex)}
                        isLast={conditionIndex === fields.length - 1}
                    />
                ))}
                <Button
                    startIcon={<AddIcon />}
                    onClick={addCondition}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                >
                    Add Condition
                </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Result Section */}
            <Box>
                <Typography variant="subtitle1" gutterBottom>
                    Result
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <RHFSelectField
                            name={`rules.${ruleIndex}.result.type`}
                            label="Result Type"
                            options={resultTypeOptions}
                            width="100%"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RHFMultipleSelectField
                            name={`rules.${ruleIndex}.result.values`}
                            label="Values"
                            options={courierOptions || []}
                            width="100%"
                        />
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
}

// Individual Condition Item Component
function ConditionItem({ ruleIndex, conditionIndex, control, watch, onRemove, isLast }) {
    const currentCondition = watch(`rules.${ruleIndex}.conditions.${conditionIndex}`);
    const selectedFact = currentCondition?.fact;

    // Get value options based on selected fact
    const getValueOptions = (fact) => {
        switch (fact) {
            case 'DELIVERY_TYPE':
                return [
                    { label: 'Forward', value: 'FORWARD' },
                    { label: 'Reverse', value: 'REVERSE' }
                ];
            case 'PAYMENT_MODE':
                return [
                    { label: 'Prepaid', value: 'PREPAID' },
                    { label: 'COD', value: 'COD' }
                ];
            default:
                return [];
        }
    };

    const valueOptions = getValueOptions(selectedFact);
    const showValueSelect = valueOptions.length > 0;

    return (
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                    <RHFSelectField
                        name={`rules.${ruleIndex}.conditions.${conditionIndex}.fact`}
                        label="Fact"
                        options={factOptions}
                        width="100%"
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <RHFSelectField
                        name={`rules.${ruleIndex}.conditions.${conditionIndex}.operator`}
                        label="Operator"
                        options={operatorOptions}
                        width="100%"
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    {showValueSelect ? (
                        <RHFSelectField
                            name={`rules.${ruleIndex}.conditions.${conditionIndex}.value`}
                            label="Value"
                            options={valueOptions}
                            width="100%"
                        />
                    ) : (
                        <RHFTextField
                            name={`rules.${ruleIndex}.conditions.${conditionIndex}.value`}
                            label="Value"
                            fullWidth
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={2}>
                    {!isLast && (
                        <IconButton onClick={onRemove} color="error" size="small">
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

export default CustomRule;
